import { useSyncHorizontalScroll } from '../../hooks/useSyncHorizontalScroll';
import { useCalculateFetchedItems } from '../../hooks/useCalculateFetchItems';
import React, { useRef, useCallback, useEffect, useState } from 'react';
import { useScrollFetchData } from '../../hooks/useScrollFetchData';
import CustomHeaderCell from '../../utils/customHeaderCell';
import { useNavigate } from 'react-router-dom';
import RegionListData from './regionListData';
import { PersistedFixedSizeList as FixedSizeList } from "../../hooks/listNavigation";
import { Box } from '@mui/material';
import { debounce } from 'lodash';

const defaultColumnWidth = {
    name: 500, description: 800
};

const RegionList = React.memo(({ scrollRef, regions, sortBy, setSortBy, setOffset, filteredRegionCount, setRegionFetched }) => {

    const navigate = useNavigate();

    const [regionColumnWidth, setRegionColumnWidth] = useState(() => {
        const storedWidths = JSON.parse(localStorage.getItem('regionColumnWidth')) || {};
        return { ...defaultColumnWidth, ...storedWidths }; // Merge defaults with stored widths
    });

    const [windowHeight, setWindowHeight] = useState(window.innerHeight);

    // Update window height on resize
    useEffect(() => {
        const handleResize = debounce(() => {
            setWindowHeight(window.innerHeight);
        }, 300); // 300ms debounce delay

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            handleResize.cancel(); // Cancel any pending debounced calls
        };
    }, []);

    const tableHeadRef = useRef(null);

    const handleColumnWidthChange = (value, columnKey) => {
        setRegionColumnWidth({ ...regionColumnWidth, [columnKey]: value })
    }

    useEffect(() => {
        localStorage.setItem('regionColumnWidth', JSON.stringify(regionColumnWidth));
    }, [regionColumnWidth]);

    // synchronize horizontal scroll between table head and list
    useSyncHorizontalScroll(tableHeadRef, scrollRef);

    // Calculate fetched items on mount
    useCalculateFetchedItems(filteredRegionCount, setRegionFetched, 70, 280);

    // Handle scroll and fetch more items
    const handleScrollList = useScrollFetchData(setOffset, setRegionFetched, filteredRegionCount, 70, 280);

    const handleRegionClick = useCallback((regionId) => navigate(`region_details/${regionId}`), [navigate]);

    const handleSort = (field) => {
        setSortBy((prevSortBy) => {
            const nextSortOrder = prevSortBy[field] === null ? 'asc'
                : prevSortBy[field] === 'asc' ? 'desc'
                    : null;

            return Object.fromEntries(
                Object.keys(prevSortBy)?.map((key) => [key, key === field ? nextSortOrder : null])
            );
        });

    };

    const Row = ({ index, style, data }) => {
        const { regions, handleRegionClick } = data;
        const region = index === 0 ? regions[0] : regions[index];
        return (
            <Box key={index} style={{ ...style, width: 'fit-content' }}>
                <RegionListData
                    region={region}
                    columnWidth={regionColumnWidth}
                    handleRegionClick={handleRegionClick}
                />
            </Box>
        );
    };

    return (
        <Box className='table'>
            <Box className='tableHead' ref={tableHeadRef}>
                <CustomHeaderCell
                    className='table_header_cell_non_filter'
                    label='Nom'
                    columnKey='name'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFilter={false}
                    isFixed={true}
                    columnWidth={regionColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    className='table_header_cell_non_filter'
                    label='Description'
                    columnKey='description'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFilter={false}
                    columnWidth={regionColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    className='table_header_cell_non_filter' width="50px" />
            </Box>
            <Box className='tableBody'>
                <FixedSizeList
                    ref={scrollRef}
                    height={Math.min(
                        filteredRegionCount * 70 + 15,
                        windowHeight - 285
                    )}
                    width='100%'
                    itemCount={filteredRegionCount}
                    itemSize={70}
                    itemData={{ regions, handleRegionClick }}
                    onScroll={handleScrollList}
                >
                    {Row}
                </FixedSizeList>
            </Box>
        </Box>

    );
});

export default RegionList;